import React, { useEffect } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
/* Constants */
import { MOBILE_AND_TABLET } from 'src/containers/AddSkuPageV2/constants';
/* Utils */
import { filterRemoteQuestions } from 'src/containers/AddSkuPage/utils';
/* Components && Styles */
import { Grid } from 'src/components/HTKit/Grid';
import Button, { THEMES } from 'src/components/HTKit/Forms/Button';
import Icon from 'src/components/HTKit/Icon';
import ButtonFooter from 'src/components/ButtonFooter';
import PreQuestion from 'src/components/PreQuestion';
import AddSkuV2BannerMessaging from './AddSkuV2BannerMessaging';
import AddSkuV2DisclaimerText from './AddSkuV2DisclaimerText';
import AddSkuV2Summary from './AddSkuV2Summary';
import AddSkuV2SkuImage from './AddSkuV2SkuImage';
import AddSkuV2SkuBullets from './AddSkuV2SkuBullets';
import styles from './styles.scss';

const AddToCartButton = ({ isConfirmation, inEditSkuMode, onClick, className }) => {
  let buttonText = 'Add to Cart';

  if (isConfirmation) buttonText = 'Next';
  if (inEditSkuMode) buttonText = 'Update';

  return (
    <Button className={className} theme={THEMES.V2PRIMARY} type="button" onClick={onClick} hugeV2>
      <Icon name="cart" className="marginRight-tiny1" />
      {buttonText}
    </Button>
  );
};
AddToCartButton.propTypes = {
  isConfirmation: PropTypes.bool,
  inEditSkuMode: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

const AddSkuPageV2 = (props) => {
  const {
    page,
    sku,
    item,
    cart,
    questions,
    addToCart: addToCartAction,
    confirmItem: confirmItemAction,
    isConfirmation,
    inEditSkuMode,
  } = props;
  /* Data */
  const skuQuestions = filterRemoteQuestions({ questions, sku, item, cart });
  /* Conditionals */
  const hasBulletsOrDisclaimer = sku
    ? sku.getIn(['skuMessagings', 'bullets']).size || sku.getIn(['skuMessagings', 'disclaimer'])
    : false;
  const hasBannerMessaging = sku.getIn(['skuMessagings', 'banner']);

  /* Methods */
  const addItemToCart = () => {
    /*
      If/when this page becomes the standard AddSkuPage we'll have to revisit this modal
      that shows for partner orders. See original AddSkuPage for more details -GH May 2, 2022

      const showItemRemovalDialog = checkPartnerCart(cart, sku).showDialog;
      if (showItemRemovalDialog) {
        this.elements.removeItemsModal.open();
      } else {
        addToCartAction();
      }
    */
    addToCartAction();
  };

  const buttonAction = isConfirmation || inEditSkuMode ? confirmItemAction : addItemToCart;

  return (
    <>
      {hasBannerMessaging && (
        <AddSkuV2BannerMessaging sku={sku} className={styles.showDesktopOnly} />
      )}
      <Grid.Fluid classes={styles.grid}>
        <AddSkuV2Summary.Fixed sku={sku} className={cn(styles.showMobileTabletOnly)} />
        <Grid.Row>
          <Grid.Column sm={4} md={8} lg={6}>
            <AddSkuV2SkuImage
              type={MOBILE_AND_TABLET}
              className="marginBottom-medium1"
              sku={sku}
              withBorderRadius
            />

            {hasBannerMessaging && (
              <AddSkuV2BannerMessaging
                sku={sku}
                className={cn(
                  'marginBottom-medium1',
                  styles.showMobileTabletOnly,
                  styles.mobileTabletSkuBanner,
                )}
              />
            )}

            <h1 className={cn('h3 marginBottom-medium', styles.pageHeader)}>
              Tell us a bit more about your service
            </h1>

            {hasBulletsOrDisclaimer && (
              <div className="marginBottom-medium1">
                <AddSkuV2SkuBullets sku={sku} className="marginBottom-small" />
                <AddSkuV2DisclaimerText sku={sku} className="marginBottom-small" />
              </div>
            )}

            {skuQuestions.map((q, index) => (
              <div key={index} className="marginBottom-medium1">
                <PreQuestion
                  question={q}
                  sku={sku}
                  error={page.getIn(['errors', q.get('id').toString()])}
                />
              </div>
            ))}
          </Grid.Column>
          <Grid.Column lgOffset={1} lg={5} classes={styles.showDesktopOnly}>
            <AddSkuV2Summary.Static
              sku={sku}
              className={styles.staticSummaryContainer}
              ctaButton={
                <AddToCartButton
                  isConfirmation={isConfirmation}
                  inEditSkuMode={inEditSkuMode}
                  onClick={buttonAction}
                  className="marginTop-small2"
                />
              }
            />
          </Grid.Column>
        </Grid.Row>
      </Grid.Fluid>
      <div className="paddingY-medium2" /> {/* Allow extra room for dropdown components */}
      <div className="paddingY-medium2" /> {/* Allow extra room for dropdown components */}
      <ButtonFooter className={styles.buttonFooterContainer}>
        <AddToCartButton
          isConfirmation={isConfirmation}
          inEditSkuMode={inEditSkuMode}
          onClick={buttonAction}
        />
      </ButtonFooter>
    </>
  );
};

AddSkuPageV2.propTypes = {
  sku: PropTypes.object,
  page: PropTypes.object.isRequired,
  cart: PropTypes.object,
  item: PropTypes.object.isRequired,
  questions: PropTypes.object,
  addToCart: PropTypes.func.isRequired,
  confirmItem: PropTypes.func.isRequired,
  isConfirmation: PropTypes.bool,
  inEditSkuMode: PropTypes.bool,
};

export default AddSkuPageV2;
